import { Link } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import type { GameState, ReplayDetail } from '@ccc/shared-types';
import { createEngine, createInitialGameState, resetPieceIdCounter } from '@ccc/rule-engine';
import { applyStartingSetupForCard, modifiersForGame } from '@ccc/cards';
import { ChessBoard } from '../components/ChessBoard';
import { Panel } from '../components/Card';
import { Button } from '../components/Button';
import { NoScroll } from '../components/Layout';
import { apiJson, loadReplay } from '../lib/multiplayer';
import { cardById } from '../lib/localGame';
import { useAuthStore } from '../lib/auth';

export function ReplayDetailRoute() {
  const gameId = window.location.pathname.split('/').filter(Boolean).at(-1) ?? '';
  const [replay, setReplay] = useState<ReplayDetail | null>(null);
  const [ply, setPly] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const user = useAuthStore((s) => s.user);
  const isOwner = user?.canAdmin && user.username === 'FM';

  useEffect(() => {
    let alive = true;
    loadReplay(gameId)
      .then((data) => {
        if (alive) {
          setReplay(data);
          setPly(replayActions(data).length);
        }
      })
      .catch((err) => {
        if (alive) setError(err instanceof Error ? err.message : 'Could not load this replay.');
      });
    return () => {
      alive = false;
    };
  }, [gameId]);

  const states = useMemo(() => (replay ? replayStates(replay) : []), [replay]);
  const state = states[ply] ?? replay?.finalState ?? null;

  async function toggleLike() {
    if (!replay) return;
    try {
      await apiJson(`/api/games/${replay.id}/like`, {
        method: replay.likedByMe ? 'DELETE' : 'POST',
        body: '{}',
      });
      setReplay({
        ...replay,
        likedByMe: !replay.likedByMe,
        likeCount: Math.max(0, replay.likeCount + (replay.likedByMe ? -1 : 1)),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Log in to like replays.');
    }
  }

  async function ownerReplayAction(action: 'hide' | 'unhide' | 'delete') {
    if (!replay) return;
    try {
      if (action === 'delete') {
        if (!window.confirm('Permanently delete this replay and its moves?')) return;
        await apiJson(`/api/owner/replays/${replay.id}`, { method: 'DELETE' });
        window.location.href = '/replays';
        return;
      }
      await apiJson(`/api/owner/replays/${replay.id}/${action}`, { method: 'POST', body: '{}' });
      setReplay({ ...replay, hidden: action === 'hide' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Owner action failed.');
    }
  }

  if (!replay || !state) {
    return (
      <div className="h-full w-full grid place-items-center px-5">
        <NoScroll />
        <Panel className="p-6 text-center">
          <div className="font-display text-2xl font-bold">Replay</div>
          <p className="mt-2 text-sm text-ink-muted">{error ?? 'Loading game record...'}</p>
          <Link to="/replays" className="inline-block mt-4">
            <Button variant="secondary">Back to replays</Button>
          </Link>
        </Panel>
      </div>
    );
  }

  const whiteCard = replay.whiteCardId ? cardById(replay.whiteCardId) : null;
  const blackCard = replay.blackCardId ? cardById(replay.blackCardId) : null;

  return (
    <div className="h-full w-full max-w-[1400px] mx-auto px-3 md:px-8 py-3 grid grid-rows-[auto_1fr] gap-3 overflow-hidden">
      <NoScroll />
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display text-2xl md:text-3xl font-bold truncate">
            {replay.whiteName} vs {replay.blackName}
          </h1>
          <p className="text-sm text-ink-muted">
            {replay.winner === 'draw' ? 'Draw' : `${replay.winner} won`} · {replay.rated ? 'Rated' : 'Casual'} ·{' '}
            {replay.likeCount} likes{replay.hidden ? ' · Hidden' : ''}
          </p>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={toggleLike}>
            {replay.likedByMe ? 'Unlike' : 'Like'}
          </Button>
          {isOwner && (
            <>
              <Button variant="secondary" size="sm" onClick={() => void ownerReplayAction(replay.hidden ? 'unhide' : 'hide')}>
                {replay.hidden ? 'Unhide' : 'Hide'}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => void ownerReplayAction('delete')}>
                Delete
              </Button>
            </>
          )}
          <Link to="/replays">
            <Button variant="secondary" size="sm">Back</Button>
          </Link>
        </div>
      </div>
      <div className="min-h-0 grid lg:grid-cols-[1fr_minmax(0,min(640px,calc(100vh-8rem)))_1fr] gap-3 md:gap-5 overflow-hidden">
        <aside className="hidden lg:flex flex-col justify-end min-h-0 overflow-hidden">
          <Panel className="p-4">
            <ReplayCard name={replay.blackName} card={blackCard} />
          </Panel>
        </aside>
        <div className="min-h-0 grid place-items-center overflow-hidden">
          <div className="aspect-square max-h-full max-w-full">
            <ChessBoard state={state} playableSide={null} />
          </div>
        </div>
        <aside className="min-h-0 flex flex-col gap-3 overflow-hidden">
          <Panel className="p-4 shrink-0">
            <ReplayCard name={replay.whiteName} card={whiteCard} />
          </Panel>
          <Panel className="p-4 shrink-0">
            <div className="flex items-center justify-between">
              <Button size="sm" variant="secondary" onClick={() => setPly(0)}>
                Start
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setPly((p) => Math.max(0, p - 1))}>
                Back
              </Button>
              <div className="font-mono text-sm">
                {ply}/{replayActions(replay).length}
              </div>
              <Button size="sm" variant="secondary" onClick={() => setPly((p) => Math.min(replayActions(replay).length, p + 1))}>
                Next
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setPly(replayActions(replay).length)}>
                End
              </Button>
            </div>
          </Panel>
          <Panel className="p-4 min-h-0 flex flex-col">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-ink-muted shrink-0">Moves</h2>
            <div className="mt-2 min-h-0 overflow-auto font-mono text-sm text-ink-muted">
              {replayActions(replay).map((action, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setPly(i + 1)}
                  className={`block w-full text-left rounded px-2 py-1 focus-ring ${
                    ply === i + 1 ? 'bg-accent/10 text-ink' : 'hover:bg-ink/[0.04]'
                  }`}
                >
                  {actionLabel(action, i)}
                </button>
              ))}
            </div>
          </Panel>
          {error && <div className="text-sm text-danger">{error}</div>}
        </aside>
      </div>
    </div>
  );
}

function ReplayCard({ name, card }: { name: string; card: ReturnType<typeof cardById> | null }) {
  return (
    <div>
      <div className="font-semibold">{name}</div>
      {card ? (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-muted">{card.category}</div>
          <div className="font-display font-semibold">{card.shortTitle}</div>
          <p className="mt-1 text-xs text-ink-muted">{card.description}</p>
        </div>
      ) : (
        <div className="mt-3 text-xs text-ink-muted">No card.</div>
      )}
    </div>
  );
}

function replayStates(replay: ReplayDetail): GameState[] {
  if (!replay.whiteCardId || !replay.blackCardId) return replay.finalState ? [replay.finalState] : [];
  resetPieceIdCounter();
  const initial = createInitialGameState({
    id: replay.id,
    initialSeconds: replay.initialSeconds,
    incrementSeconds: replay.incrementSeconds,
    rated: replay.rated,
    activeRules: { white: replay.whiteCardId, black: replay.blackCardId },
  });
  initial.status = 'active';
  applyStartingSetupForCard(initial, replay.whiteCardId, 'white');
  applyStartingSetupForCard(initial, replay.blackCardId, 'black');
  const engine = createEngine(
    modifiersForGame({ whiteCard: replay.whiteCardId, blackCard: replay.blackCardId }),
  );
  const states: GameState[] = [structuredClone(initial)];
  let cur = initial;
  for (const action of replayActions(replay)) {
    cur = engine.applyAction(cur, action).state;
    states.push(structuredClone(cur));
  }
  if (replay.finalState) states[states.length - 1] = replay.finalState;
  return states;
}

function replayActions(replay: ReplayDetail) {
  return replay.actions?.length ? replay.actions : replay.moves.map((move) => ({ kind: 'move' as const, move }));
}

function actionLabel(action: ReturnType<typeof replayActions>[number], index: number): string {
  if (action.kind === 'store_tempo') return `${Math.floor(index / 2) + 1}. ${action.side} stores tempo`;
  return `${Math.floor(index / 2) + 1}.${index % 2 ? '..' : ''} ${action.move.from}-${action.move.to}`;
}
